<template>
<ShortcutWrapper :nShortcutRequiredClaim="UserClaims.AccountManager" @n-shortcut="parseTableAction({ actionType: 'create-account' })">
  <v-container fluid>
    <Dialog
      :stateId="dialogId"
      @dialog-closing="resetDialogs"
    >
      <ConfirmDelete
      v-if="deleting"
      :title="$t('account')"
      @delete="deleteItem"
      @cancel-delete="resetDialogs"/>
      <AccountForm
      @account-changed="refreshAccounts"
      @account-policies-changed="fetchAccounts(tags)"
      v-if="creating || editing"
      :isEditing="editing"
      :propAccountId="focusedAccount.accountId"
      />
      <AccountDetail
      :accountId="focusedAccount.accountId"
      v-if="accountDetails"
      @edit-account="editAccount"
      @show-qrcode="showQrCode"
      @show-driver-qrcode="showDriverQrCode"
      />
      <AccountQrCode
      v-if="qrCodeDialog"
      :propAccount="focusedAccount"
      @click:details="viewAccountDetails"
      />
      <DriverQrCode
      v-if="driverQrCodeDialog"
      :propAccount="focusedAccount"
      @click:details="viewAccountDetails"
      />
      <PayablesByTract @close="resetDialogs" v-if="viewingPayables" :account="focusedAccount"/>
      <TicketBooks v-if="viewTicketBooks" @close="resetDialogs" :account="focusedAccount"/>
      <AccountQrDownloadDialog
        v-if="qrCodeTypeDialog"
        :isDownloading="isDownloading"
        :downloadProgress="downloadProgress"
        :downloadTotal="downloadTotal"
        @close-dialog="resetDialogs"
        @download="downloadQrCodes"
      />
      <EudrManagerDialog
        v-if="showingEudrManager"
        @close="resetDialogs"
      />
    </Dialog>
    <AccountTable
    @tags-chosen="applyFilter"
    @refresh-accounts="refreshAccounts"
    @account-action="parseTableAction"
    @ticket-books="openTicketBooks"
    />
  </v-container>
</ShortcutWrapper>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { UserClaims } from '@/utils/Enumerations'
import { DialogWidth } from '@/utils/Dialog.js'
import { generateAccountDriverSheetPdf, generateAccountIdSheetPDF, getAccountQrCodeData, prefetchDriverSheetLogo, buildDriverSheetPages } from '@/utils/pdf-formatters/AccountIdentification'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { getLegnaQrCodeUrl } from '@/utils/LegnaQrCode'

export default {
  name: 'Accounts',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    AccountForm: () => import('@/components/account/AccountForm.vue'),
    AccountTable: () => import('@/components/account/AccountTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    PayablesByTract: () => import('@/components/accounting/accounts-payable/PayablesByTractDialog.vue'),
    AccountDetail: () => import('@/components/account/account-detail/AccountDetail.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    AccountQrCode: () => import('@/components/account/AccountQrCode.vue'),
    DriverQrCode: () => import('@/components/account/DriverQrCode.vue'),
    TicketBooks: () => import('@/components/account/ticketBooks/TicketBooks.vue'),
    AccountQrDownloadDialog: () => import('@/components/account/AccountQrDownloadDialog.vue'),
    EudrManagerDialog: () => import('@/components/account/account-eudr/EudrManagerDialog.vue')
  },

  data: () => ({
    dialogId: 'accounts-dialog',
    deleting: false,
    editing: false,
    qrCodeDialog: false,
    driverQrCodeDialog: false,
    qrCodeTypeDialog: false,
    creating: false,
    viewingPayables: false,
    accountDetails: false,
    viewTicketBooks: false,
    showingEudrManager: false,
    tags: [],
    focusedAccount: {},
    UserClaims,
    isDownloading: false,
    downloadProgress: 0,
    downloadTotal: 0
  }),

  async created () {
    this.refreshAccounts()

    if (this.routerJump != null) {
      await this.handleRouterJump(this.routerJump)
    }
  },

  computed: {
    ...mapGetters('global', ['routerJump']),
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('user', ['companyInfo'])
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('account', ['fetchAccounts', 'deleteAccount', 'fetchAccountById']),
    ...mapActions('account-driver-details', ['fetchDriverDetail']),
    generateAccountIdSheetPDF,
    generateAccountDriverSheetPdf,

    parseTableAction (action) {
      this.resetDialogs()

      if (action.account) {
        this.focusedAccount = action.account
      }

      switch (action.actionType) {
        case 'create-account':
          this.creating = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.StandardNarrow })
          break
        case 'edit-account':
          this.editing = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.StandardWide })
          break
        case 'view-account-details':
          this.accountDetails = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Standard })
          break
        case 'view-payables':
          this.viewingPayables = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Standard })
          break
        case 'delete-account':
          this.deleting = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Simple, allowFullscreen: false })
          break
        case 'show-account-qrcode':
          this.qrCodeDialog = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Standard })
          break
        case 'show-driver-qrcode':
          this.driverQrCodeDialog = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.Standard })
          break
        case 'open-account-qr-download-dialog':
          this.qrCodeTypeDialog = true
          this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.SimpleWide })
          break
        case 'open-eudr-manager':
          this.showingEudrManager = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '95vw' })
          break
        default: break
      }
    },

    showQrCode (account) {
      this.parseTableAction({ actionType: 'show-account-qrcode', account })
    },

    showDriverQrCode (account) {
      this.parseTableAction({ actionType: 'show-driver-qrcode', account })
    },

    viewAccountDetails (account) {
      this.parseTableAction({ actionType: 'view-account-details', account })
    },

    async downloadQrCodes ({ type, selectedTags, individualTrailers = false }) {
      const selectedTagValues = (selectedTags ?? []).map(t => t.value)
      const accountsToDownload = this.allAccounts.filter(a => {
        if (!(a.code || type === 'driver') || !a.isExternal) return false
        if (selectedTagValues.length === 0) return true
        return (a.accountTags ?? []).some(tag => selectedTagValues.includes(tag))
      })

      this.isDownloading = true
      this.downloadTotal = accountsToDownload.length
      this.downloadProgress = 0

      const blobs = {}
      try {
        switch (type) {
          case 'account':
            for (const account of accountsToDownload) {
              const text = getAccountQrCodeData(account, this.companyInfo)
              const qrCode = await getLegnaQrCodeUrl(250, text)
              const pdf = await generateAccountIdSheetPDF(account, qrCode, false)
              blobs[pdf.fileName] = pdf.fileData
              this.downloadProgress++
            }
            break
          case 'driver': {
            const logoData = await prefetchDriverSheetLogo(this.companyInfo)
            const trailersByAccount = {}
            if (individualTrailers) {
              const needDetails = accountsToDownload.filter(a => a.accountDriverDetailId != null)
              await Promise.all(needDetails.map(async a => {
                const driverDetail = await this.fetchDriverDetail({
                  accountId: a.accountId,
                  driverDetailId: a.accountDriverDetailId
                })
                trailersByAccount[a.accountId] = driverDetail?.trailerIdentifiers ?? []
              }))
            }
            for (const account of accountsToDownload) {
              const pages = await buildDriverSheetPages(account, this.companyInfo, trailersByAccount[account.accountId] ?? [])
              const pdf = await generateAccountDriverSheetPdf(account, pages, false, logoData)
              blobs[pdf.fileName] = pdf.fileData
              this.downloadProgress++
            }
            break
          }
          default:
            return
        }
        await this.exportZipFile(blobs, `account-${type}-qr-codes`)
      } finally {
        this.resetDialogs()
      }
    },

    async exportZipFile (files, ZIPFileName) {
      const zip = new JSZip()
      Object.entries(files).forEach(([fileName, fileData]) => {
        zip.file(fileName, fileData)
      })
      return zip.generateAsync({ type: 'blob' }).then(content => {
        saveAs(content, `${ZIPFileName}.zip`)
      })
    },

    async deleteItem () {
      await this.deleteAccount(this.focusedAccount.accountId)
      this.resetDialogs()
      this.refreshAccounts()
    },

    resetDialogs () {
      this.focusedAccount = {}
      this.closeDialogsAtOrAbove(this.dialogId)
      this.editing = false
      this.creating = false
      this.deleting = false
      this.accountDetails = false
      this.viewingPayables = false
      this.qrCodeDialog = false
      this.viewTicketBooks = false
      this.showingEudrManager = false
      this.driverQrCodeDialog = false
      this.qrCodeTypeDialog = false
      this.isDownloading = false
      this.downloadProgress = 0
      this.downloadTotal = 0
    },

    refreshAccounts () {
      this.resetDialogs()
      this.fetchAccounts(this.tags)
    },

    editAccount (account) {
      this.resetDialogs()
      this.focusedAccount = account
      this.editing = true
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.StandardWide })
    },

    applyFilter (tags) {
      this.tags = tags
      this.refreshAccounts()
    },

    async handleRouterJump (jump) {
      const account = this.allAccounts
        .find(a => a.accountId === jump.sourceObject.accountId) ?? await this.fetchAccountById(jump.sourceObject.accountId)

      this.$nextTick(_ => {
        this.editAccount(account)
      })
    },

    openTicketBooks () {
      this.resetDialogs()
      this.viewTicketBooks = true
      this.openOrUpdateDialog({ id: this.dialogId, width: 1200 })
    }
  }
}
</script>

<style>
  #progress-bar-table {
    position: absolute;
    left: 50%;
    top: 30%;
  }
</style>
